/**
 * What a logged-out visitor's tap on an offer should do.
 *
 * Extracted because the bug this replaces was one of ordering, not logic. The call site is a long
 * `handlePromotionActionClick` whose branches are evaluated top to bottom, and moving the anonymous
 * branch above the in-store and free-service ones silently turned "sign in to redeem this at the
 * desk" into "record it as a discount on the cart". Nothing failed; the wrong thing simply happened.
 * As a function the order is one readable expression and can be asserted.
 *
 * The three outcomes:
 *
 * - `sign-in` — acting on the offer records a redemption against a person. In-store offers are
 *   redeemed at the front desk and free-service offers hand over a chosen service, so neither can be
 *   claimed on a cart. Also the answer when there is nothing to record a claim against.
 * - `pick-requirements` — the offer needs items chosen before it means anything; the picker opens and
 *   the chosen items go to the pending cart tagged with the offer.
 * - `claim` — an ordinary cart discount, including one with nothing to add such as a percentage off
 *   everything. Claimed now, applied once the account exists, and reported if it does not hold.
 */
export type VisitorOfferAction = "sign-in" | "pick-requirements" | "claim";

export const visitorOfferAction = (offer: {
  /** Redeemed at the practice against the patient who presents it. */
  isInStoreOffer: boolean;
  /** Hands a chosen service to a named patient. */
  isForFreeService: boolean;
  /** The offer has items to choose before it applies. */
  opensRequirementSelection: boolean;
  /** A discount id and a platform to record the claim against; without both there is nothing to store. */
  isClaimable: boolean;
}): VisitorOfferAction => {
  if (offer.isInStoreOffer || offer.isForFreeService) {
    return "sign-in";
  }
  if (offer.opensRequirementSelection) {
    return "pick-requirements";
  }
  return offer.isClaimable ? "claim" : "sign-in";
};
